import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Apple as AppleIcon, Android as AndroidIcon } from '@mui/icons-material';

// ─── Konklave brand palette (crimson + gold) ─────────────────────────────────
export const CRIMSON = '#A6192E';
export const CRIMSON_DEEP = '#7A1222';
export const CRIMSON_DARK = '#5E0D19';
export const CRIMSON_LIGHT = '#C8304A';
export const GOLD = '#D4A11E';
export const GOLD_BRIGHT = '#FFD700';
export const CREAM = '#FBF7F1';
export const CREAM_ALT = '#F4EBDF';
export const INK = '#2B1619';
export const MUTED = '#6A5558';

export const A = '/brand/konklave';
export const BRAND = 'Konklave';
export const ROOT_DOMAIN = 'konklave.app';
export const CONTACT_EMAIL = 'hello@konklave.app';
export const ADMIN_CONSOLE_URL = 'https://admin.konklave.app';
export const IOS_URL = 'https://apps.apple.com/us/app/nupe-loop/id6755416369';
export const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.nupeloop';

/** Web app address for a community (orgId === subdomain). */
const KNOWN_HOSTS: Record<string, string> = {
  kappa: 'https://members.konklave.app',
  dst: 'https://members.crimsonconnect.app',
};
export const communityUrl = (orgId: string) =>
  KNOWN_HOSTS[orgId] || `https://${orgId}.${ROOT_DOMAIN}`;

// Fictional example communities used in illustrations.
export const TENANTS = [
  { name: 'Alpha Omega Fraternity', short: 'ΑΩ', tone: '#1C2E54', accent: '#C9A646', host: 'alphaomega' },
  { name: 'Sigma Theta Sorority', short: 'ΣΘ', tone: '#8C1D2C', accent: '#F2D6A2', host: 'sigmatheta' },
  { name: 'Heritage Civic Council', short: 'HC', tone: '#1E5C3A', accent: '#E7C76A', host: 'heritage' },
];

// Internal page routes use SPA <Link>; hash anchors / external links use <a>.
export const Lnk: React.FC<{
  to: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  'aria-label'?: string;
}> = ({ to, className, style, children, ...rest }) => {
  const internal = to.startsWith('/') && !to.includes('#');
  return internal ? (
    <Link to={to} className={className} style={style} {...rest}>{children}</Link>
  ) : (
    <a href={to} className={className} style={style} {...rest}>{children}</a>
  );
};

export function usePageMeta(title: string) {
  useEffect(() => {
    document.title = `${title} — ${BRAND}`;
  }, [title]);
}

/** App Store / Google Play download buttons. */
export const StoreBadges: React.FC<{ dark?: boolean }> = ({ dark }) => (
  <div className={`stores${dark ? ' on-dark' : ''}`}>
    <a className="store" href={IOS_URL} target="_blank" rel="noopener noreferrer">
      <AppleIcon aria-hidden="true" />
      <span><small>Download on the</small>App Store</span>
    </a>
    <a className="store" href={ANDROID_URL} target="_blank" rel="noopener noreferrer">
      <AndroidIcon aria-hidden="true" />
      <span><small>Get it on</small>Google Play</span>
    </a>
  </div>
);

/** Reusable bottom call-to-action band used across sub-pages. */
export const BottomCta: React.FC<{ title?: string }> = ({
  title = 'Give your members one place to gather.',
}) => (
  <section className="band band-crimson">
    <div className="container bottom-cta">
      <h2>{title}</h2>
      <div className="hero-cta center-cta">
        <Lnk to="/get-started" className="btn btn-gold">Create your community</Lnk>
        <Lnk to="/contact" className="btn btn-ghost-light">Request a demo</Lnk>
      </div>
    </div>
  </section>
);

export const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Instrument+Sans:wght@400;500;600;700&family=Josefin+Sans:wght@600&display=swap');
:where(.kk) * { box-sizing: border-box; margin: 0; }
.kk { font-family: 'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: ${INK}; background: ${CREAM}; line-height: 1.6; -webkit-font-smoothing: antialiased; overflow-x: hidden; }
.kk h1, .kk h2, .kk h3, .kk h4 { font-family: 'Fraunces', Georgia, serif; color: ${INK}; line-height: 1.1; font-weight: 600; font-optical-sizing: auto; letter-spacing: -.01em; }
.kk a { color: inherit; text-decoration: none; }
.kk :focus-visible { outline: 3px solid ${CRIMSON_DARK}; outline-offset: 3px; box-shadow: 0 0 0 3px #fff; border-radius: 6px; }
.kk :focus-visible { scroll-margin-block: 110px; }
.kk .footer :focus-visible, .kk .band-crimson :focus-visible { outline-color: #fff; box-shadow: 0 0 0 3px ${CRIMSON_DARK}; }
.kk #main { scroll-margin-top: 90px; }
.kk img { max-width: 100%; }
.kk input::placeholder, .kk textarea::placeholder { color: ${MUTED}; opacity: 1; }
.skip { position: absolute; left: -9999px; top: 8px; background: ${CRIMSON}; color: #fff !important; padding: 10px 14px; border-radius: 8px; z-index: 100; }
.skip:focus { left: 12px; }
.container { max-width: 1180px; margin: 0 auto; padding: 0 28px; }
.center { text-align: center; }
.muted { color: ${MUTED}; }
.small { font-size: 0.92rem; }
.xs { font-size: 0.84rem; line-height: 1.5; }
.section-title { font-size: clamp(1.9rem, 3.6vw, 2.75rem); margin-bottom: 14px; }
.section-lead { color: ${MUTED}; font-size: 1.06rem; max-width: 600px; }
.light { color: #fff !important; }

/* ── Buttons ── */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-family: inherit; font-weight: 600; font-size: 0.98rem; border-radius: 999px; cursor: pointer; transition: background .16s ease, color .16s ease, border-color .16s ease, box-shadow .16s ease; border: 1.5px solid transparent; padding: 12px 24px; line-height: 1.2; text-align: center; }
.btn:disabled { opacity: .55; cursor: not-allowed; }
.kk .btn-primary { background: ${CRIMSON}; color: #fff; box-shadow: 0 6px 18px rgba(166,25,46,.24); }
.kk .btn-primary:hover:not(:disabled) { background: ${CRIMSON_DEEP}; }
.kk .btn-outline { background: transparent; color: ${CRIMSON_DEEP}; border-color: rgba(122,18,34,.35); }
.kk .btn-outline:hover:not(:disabled) { border-color: ${CRIMSON}; background: rgba(166,25,46,.05); }
.kk .btn-gold { background: ${GOLD}; color: ${CRIMSON_DARK}; }
.kk .btn-gold:hover:not(:disabled) { background: #E2B23A; }
.kk .btn-ghost-light { background: transparent; color: #fff; border-color: rgba(255,255,255,.45); }
.kk .btn-ghost-light:hover { border-color: #fff; background: rgba(255,255,255,.08); }
.kk .btn-link { color: ${CRIMSON}; padding: 12px 2px; background: none; border: none; font-weight: 600; }
.kk .btn-link:hover { color: ${CRIMSON_DEEP}; text-decoration: underline; text-underline-offset: 4px; }
.btn-block { width: 100%; }
.kk .text-link { color: ${CRIMSON}; font-weight: 600; text-decoration: underline !important; text-underline-offset: 3px; text-decoration-color: rgba(166,25,46,.35) !important; }
.kk .text-link:hover { text-decoration-color: ${CRIMSON} !important; }

/* ── Store badges ── */
.stores { display: flex; gap: 10px; flex-wrap: wrap; }
.store { display: inline-flex; align-items: center; gap: 9px; background: ${INK}; color: #fff !important; border-radius: 12px; padding: 8px 16px 8px 12px; min-height: 48px; transition: background .16s ease; }
.store:hover { background: #000; }
.store svg { font-size: 26px; }
.store span { display: flex; flex-direction: column; font-weight: 600; font-size: 1rem; line-height: 1.1; }
.store small { font-size: .68rem; font-weight: 500; opacity: .85; }
.stores.on-dark .store { background: #fff; color: ${INK} !important; }
.stores.on-dark .store:hover { background: ${CREAM_ALT}; }

/* ── Nav ── */
.nav { position: sticky; top: 0; z-index: 50; background: rgba(251,247,241,.94); backdrop-filter: blur(10px); border-bottom: 1px solid rgba(94,13,25,.08); }
.nav-inner { display: flex; align-items: center; gap: 28px; height: 74px; }
.brand { display: inline-flex; align-items: center; gap: 10px; flex-shrink: 0; }
.brand-mark { height: 40px; width: 40px; object-fit: contain; }
.brand-word { font-family: 'Josefin Sans', 'Instrument Sans', sans-serif; font-weight: 600; letter-spacing: .26em; font-size: 1.12rem; color: ${CRIMSON}; padding-top: 3px; }
.brand-word.light { color: #fff; }
.nav-links { display: flex; gap: 26px; font-weight: 500; font-size: .96rem; align-items: center; margin-left: 12px; }
.nav-links a { padding: 6px 0; border-bottom: 2px solid transparent; }
.nav-links a:hover, .nav-links a.active { color: ${CRIMSON}; border-bottom-color: ${CRIMSON}; }
.nav-actions { margin-left: auto; display: flex; align-items: center; gap: 10px; }
.nav-actions .btn { padding: 10px 18px; font-size: .92rem; }
.nav-toggle { flex-shrink: 0; display: none; background: none; border: 1.5px solid rgba(94,13,25,.2); border-radius: 10px; width: 44px; height: 44px; align-items: center; justify-content: center; color: ${CRIMSON_DEEP}; cursor: pointer; }
.nav-drawer { display: none; }

/* ── Hero ── */
.hero { position: relative; padding: 64px 0 88px; overflow: hidden; }
.hero-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; }
.hero-inner > * { min-width: 0; }
.hero h1 { font-size: clamp(2.6rem, 5.6vw, 4.4rem); line-height: 1.02; margin-bottom: 22px; color: ${CRIMSON_DARK}; }
.lead { color: ${MUTED}; font-size: 1.12rem; max-width: 540px; margin-bottom: 30px; }
.hero-cta { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.hero-sub { margin-top: 28px; display: flex; flex-direction: column; gap: 10px; }
.hero-sub p { font-size: .88rem; color: ${MUTED}; }

.hero-visual { position: relative; min-height: 540px; display: flex; justify-content: center; align-items: center; }
.ring { position: absolute; width: 520px; max-width: 120%; aspect-ratio: 1; left: calc(50% - 70px); top: 50%; transform: translate(-50%,-50%); pointer-events: none; }
.ring path { fill: none; stroke-linecap: round; }

/* CSS phone mock */
.hero-visual .phone { margin-right: 140px; }
.phone { position: relative; width: 272px; border-radius: 40px; background: #1a0d0f; padding: 9px; box-shadow: 0 40px 80px rgba(94,13,25,.28), 0 0 0 1px rgba(0,0,0,.2); z-index: 2; }
.screen { border-radius: 32px; overflow: hidden; background: #F7F2EC; font-family: 'Instrument Sans', sans-serif; }
.scr-head { background: ${CRIMSON}; color: #fff; padding: 34px 18px 18px; }
.scr-head .org { display: flex; align-items: center; gap: 9px; font-size: .78rem; font-weight: 600; opacity: .95; }
.scr-head .org i { font-style: normal; width: 26px; height: 26px; border-radius: 50%; background: rgba(255,255,255,.18); border: 1.5px solid ${GOLD}; display: inline-flex; align-items: center; justify-content: center; font-size: .68rem; }
.scr-head h5 { font-family: 'Fraunces', serif; font-size: 1.28rem; font-weight: 600; margin-top: 12px; color: #fff; }
.scr-body { padding: 12px; display: flex; flex-direction: column; gap: 9px; }
.scr-card { background: #fff; border-radius: 14px; padding: 11px 12px; display: flex; gap: 10px; align-items: center; box-shadow: 0 1px 0 rgba(0,0,0,.04); }
.scr-card .ic { width: 34px; height: 34px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; background: rgba(166,25,46,.1); color: ${CRIMSON}; }
.scr-card .ic svg { font-size: 19px; }
.scr-card b { display: block; font-size: .8rem; color: ${INK}; line-height: 1.25; }
.scr-card span { display: block; font-size: .7rem; color: ${MUTED}; line-height: 1.3; }
.scr-card .tag { margin-left: auto; font-size: .64rem; font-weight: 700; padding: 4px 8px; border-radius: 999px; background: ${CRIMSON}; color: #fff; white-space: nowrap; }
.scr-card .tag.ok { background: #E6F3EA; color: #1E6B3C; }
.scr-tabs { display: flex; justify-content: space-around; padding: 10px 6px 14px; border-top: 1px solid rgba(0,0,0,.06); background: #fff; }
.scr-tabs span { width: 22px; height: 4px; border-radius: 4px; background: #E3D8D9; }
.scr-tabs span.on { background: ${CRIMSON}; }
.float-card { position: absolute; z-index: 3; background: #fff; border-radius: 16px; padding: 12px 14px; box-shadow: 0 18px 40px rgba(94,13,25,.16); font-size: .8rem; max-width: 220px; }
.float-chat { right: 0; top: 96px; }
.float-chat .who { font-weight: 700; color: ${INK}; font-size: .74rem; margin-bottom: 3px; }
.float-chat .mention { color: ${CRIMSON}; font-weight: 600; background: rgba(166,25,46,.08); border-radius: 4px; padding: 0 3px; }
.float-tap { right: 16px; bottom: 96px; display: flex; gap: 10px; align-items: center; }
.float-tap .ic { width: 36px; height: 36px; border-radius: 50%; background: ${GOLD}; color: ${CRIMSON_DARK}; display: inline-flex; align-items: center; justify-content: center; }
.float-tap b { display: block; font-size: .84rem; }

/* ── Bands ── */
.band { padding: 88px 0; position: relative; }
.band-alt { background: ${CREAM_ALT}; }
.band-crimson { background: ${CRIMSON_DEEP}; color: #F6E7E9; }
.band-crimson h2, .band-crimson h3, .band-crimson h4 { color: #fff; }

/* Two paths */
.paths { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border-radius: 28px; overflow: hidden; box-shadow: 0 24px 60px rgba(94,13,25,.1); }
.path { padding: 44px 40px; display: flex; flex-direction: column; gap: 14px; }
.path h2 { font-size: clamp(1.6rem, 2.6vw, 2.1rem); }
.path p { max-width: 440px; }
.path ul { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 8px; margin: 4px 0 10px; }
.path li { display: flex; gap: 10px; align-items: flex-start; font-size: .96rem; }
.path li svg { font-size: 19px; margin-top: 3px; flex-shrink: 0; }
.path .btn { align-self: flex-start; margin-top: auto; }
.path-lead { background: ${CRIMSON}; color: #FBE9EB; }
.path-lead h2 { color: #fff; }
.path-lead li svg { color: ${GOLD}; }
.path-member { background: #fff; }
.path-member li svg { color: ${CRIMSON}; }

/* Feature columns */
.feat-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 32px; margin-bottom: 44px; flex-wrap: wrap; }
.feat-cols { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-top: 2px solid ${CRIMSON}; }
.feat-col { padding: 26px 24px 8px 0; }
.feat-col + .feat-col { padding-left: 24px; border-left: 1px solid rgba(94,13,25,.12); }
.feat-col h3 { font-size: 1.35rem; margin-bottom: 4px; color: ${CRIMSON_DEEP}; }
.feat-col > p { color: ${MUTED}; font-size: .92rem; margin-bottom: 18px; }
.feat-list { list-style: none; padding: 0; display: flex; flex-direction: column; gap: 16px; }
.feat-list li { display: grid; grid-template-columns: 22px 1fr; gap: 10px; }
.feat-list svg { color: ${CRIMSON}; font-size: 20px; margin-top: 2px; }
.feat-list b { display: block; font-size: .96rem; font-weight: 600; }
.feat-list span { display: block; font-size: .86rem; color: ${MUTED}; line-height: 1.45; }
.audiences { display: flex; flex-wrap: wrap; gap: 8px; }

/* Hierarchy */
.hier { display: grid; grid-template-columns: .9fr 1.1fr; gap: 56px; align-items: center; }
.hier p { color: #EBD3D7; }
.hier-points { list-style: none; padding: 0; margin-top: 22px; display: flex; flex-direction: column; gap: 12px; }
.hier-points li { display: flex; gap: 10px; align-items: flex-start; color: #F6E7E9; }
.hier-points li svg { color: ${GOLD}; font-size: 20px; margin-top: 2px; flex-shrink: 0; }
.tree { position: relative; background: rgba(0,0,0,.14); border: 1px solid rgba(255,255,255,.12); border-radius: 24px; padding: 30px 22px; }
.org, .org ul { list-style: none; padding: 0; margin: 0; }
.org ul { display: flex; justify-content: center; position: relative; padding-top: 22px; }
.org ul::before { content: ''; position: absolute; top: 0; left: 50%; height: 22px; border-left: 2px solid rgba(255,255,255,.4); }
.org li { position: relative; flex: 1 1 0; min-width: 0; text-align: center; padding: 22px 4px 0; }
.org > li { padding-top: 0; }
.org li::before, .org li::after { content: ''; position: absolute; top: 0; right: 50%; width: 50%; height: 22px; border-top: 2px solid rgba(255,255,255,.4); }
.org li::after { right: auto; left: 50%; border-left: 2px solid rgba(255,255,255,.4); }
.org > li::before, .org > li::after { display: none; }
.org li:only-child::before { display: none; }
.org li:only-child::after { border-top: none; }
.org li:first-child::before, .org li:last-child::after { border-top: none; }
.org li:last-child::before { border-right: 2px solid rgba(255,255,255,.4); border-top: 2px solid rgba(255,255,255,.4); }
.org li:last-child::after { border-left: none; }
.org li:first-child::after { border-top: 2px solid rgba(255,255,255,.4); }
.node { position: relative; z-index: 1; display: inline-block; background: #fff; color: ${INK}; border-radius: 12px; padding: 9px 12px; font-size: .8rem; font-weight: 600; text-align: center; max-width: 100%; box-shadow: 0 6px 16px rgba(0,0,0,.18); line-height: 1.25; }
.node small { display: block; color: ${MUTED}; font-weight: 500; font-size: .68rem; }
.node.hq { background: ${GOLD}; color: ${CRIMSON_DARK}; padding: 11px 18px; }
.node.hq small { color: ${CRIMSON_DEEP}; }
.node.target { outline: 2px solid ${GOLD}; outline-offset: 3px; }
.tree-note { margin-top: 22px; display: flex; gap: 8px; align-items: center; font-size: .84rem; color: #F6E7E9; }
.tree-note i { width: 12px; height: 12px; border-radius: 4px; border: 2px solid ${GOLD}; display: inline-block; flex-shrink: 0; }

/* Branded communities */
.brands { display: grid; grid-template-columns: .9fr 1.1fr; gap: 56px; align-items: center; }
.minis { display: flex; gap: 18px; justify-content: center; align-items: flex-end; }
.mini { width: 172px; border-radius: 28px; background: #1a0d0f; padding: 6px; box-shadow: 0 20px 44px rgba(43,22,25,.2); }
.mini:nth-child(2) { transform: translateY(-26px); }
.mini .screen { border-radius: 23px; }
.mini-head { padding: 24px 12px 14px; color: #fff; }
.mini-head i { font-style: normal; width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-family: 'Fraunces', serif; font-size: .82rem; border: 2px solid; margin-bottom: 8px; }
.mini-head b { display: block; font-size: .78rem; line-height: 1.2; }
.mini-head span { display: block; font-size: .62rem; opacity: .8; margin-top: 2px; }
.mini-body { padding: 9px; display: flex; flex-direction: column; gap: 6px; }
.mini-body div { height: 34px; border-radius: 9px; background: #fff; }
.mini-body div:nth-child(2) { height: 52px; }

/* Setup steps */
.steps { list-style: none; padding: 0; display: grid; grid-template-columns: repeat(4,1fr); gap: 28px; counter-reset: s; margin-top: 44px; }
.steps li { counter-increment: s; position: relative; padding-top: 58px; }
.steps li::before { content: counter(s); position: absolute; top: 0; left: 0; width: 42px; height: 42px; border-radius: 50%; border: 2px solid ${CRIMSON}; color: ${CRIMSON}; font-family: 'Fraunces', serif; font-weight: 600; font-size: 1.15rem; display: flex; align-items: center; justify-content: center; }
.steps li::after { content: ''; position: absolute; top: 21px; left: 54px; right: -16px; height: 2px; background: repeating-linear-gradient(90deg, rgba(166,25,46,.35) 0 6px, transparent 6px 12px); }
.steps li:last-child::after { display: none; }
.steps h3 { font-size: 1.2rem; margin-bottom: 6px; }
.steps p { color: ${MUTED}; font-size: .94rem; }

/* Final CTA */
.final { display: grid; grid-template-columns: auto 1fr; gap: 40px; align-items: center; }
.final img { width: 132px; }
.final h2 { font-size: clamp(1.8rem, 3.4vw, 2.6rem); margin-bottom: 10px; }
.final .hero-cta { margin-top: 22px; }

/* ── Sub-pages ── */
.page-head { background: ${CREAM_ALT}; padding: 64px 0 40px; border-bottom: 1px solid rgba(94,13,25,.08); }
.page-head h1 { font-size: clamp(2.2rem, 4.4vw, 3.2rem); margin-bottom: 12px; color: ${CRIMSON_DARK}; }
.page-head p { color: ${MUTED}; font-size: 1.08rem; max-width: 680px; }
.page-head .kicker { color: ${CRIMSON}; font-weight: 600; font-size: .95rem; margin-bottom: 8px; }
.page-body { padding: 56px 0 84px; }

.prose { max-width: 780px; }
.prose h2 { font-size: 1.55rem; margin: 34px 0 10px; }
.prose h3 { font-size: 1.2rem; margin: 22px 0 6px; }
.prose p, .prose li { color: ${INK}; font-size: 1.02rem; margin-bottom: 12px; }
.prose ul { padding-left: 22px; margin-bottom: 12px; }
.prose a { color: ${CRIMSON}; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; }
.prose .updated { color: ${MUTED}; font-size: .9rem; margin-bottom: 24px; }

.grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 20px; }
.grid-3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 18px; }
.card { background: #fff; border: 1px solid rgba(94,13,25,.08); border-radius: 18px; padding: 26px 22px; }
.card h3 { font-size: 1.2rem; margin-bottom: 8px; }

.group { margin-bottom: 56px; }
.group:last-child { margin-bottom: 0; }
.group-head { max-width: 720px; margin-bottom: 22px; }
.group-head h2 { font-size: 1.75rem; margin-bottom: 6px; }
.gold-coin { width: 46px; height: 46px; border-radius: 14px; background: rgba(166,25,46,.08); display: flex; align-items: center; justify-content: center; color: ${CRIMSON}; }
.feature-lg { background: #fff; border: 1px solid rgba(94,13,25,.08); border-radius: 18px; padding: 24px; }
.feature-lg .gold-coin { margin-bottom: 14px; }
.feature-lg h3 { font-size: 1.2rem; margin-bottom: 6px; }
.feature-lg p { color: ${MUTED}; font-size: .95rem; }
.solution-card { display: flex; flex-direction: column; background: #fff; border: 1px solid rgba(94,13,25,.08); border-top: 4px solid ${CRIMSON}; border-radius: 6px 6px 18px 18px; padding: 26px; transition: box-shadow .16s ease; }
.solution-card:hover { box-shadow: 0 16px 36px rgba(94,13,25,.12); }
.solution-card h3 { font-size: 1.4rem; margin-bottom: 6px; }
.solution-card p { color: ${MUTED}; font-size: .96rem; }
.solution-card .more { color: ${CRIMSON}; font-weight: 600; font-size: .92rem; margin-top: auto; padding-top: 16px; display: inline-flex; align-items: center; gap: 4px; }
.bottom-cta { text-align: center; }
.bottom-cta h2 { font-size: clamp(1.7rem,3.2vw,2.4rem); margin: 0 auto 6px; max-width: 760px; }
.center-cta { justify-content: center; margin-top: 24px; }
.pill-list, .audiences { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.kk .pill { background: #fff; border: 1px solid rgba(166,25,46,.22); color: ${CRIMSON_DEEP}; border-radius: 999px; padding: 6px 14px; font-size: .88rem; font-weight: 500; }
a.pill:hover { border-color: ${CRIMSON}; }
.check-list { list-style: none; padding: 0; }
.check-list li { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 14px; font-size: .98rem; }
.check-list svg { color: ${CRIMSON}; font-size: 20px; margin-top: 2px; flex-shrink: 0; }

/* Forms */
.contact-grid { display: grid; grid-template-columns: 1fr 1.15fr; gap: 48px; align-items: start; }
.contact-aside h2 { font-size: 1.7rem; margin-bottom: 12px; }
.contact-aside p { color: ${MUTED}; margin-bottom: 20px; }
.form-card { background: #fff; border: 1px solid rgba(94,13,25,.1); border-radius: 22px; padding: 30px; box-shadow: 0 14px 36px rgba(94,13,25,.07); }
.field { margin-bottom: 16px; }
.field label, .field .label { display: block; font-size: .88rem; font-weight: 600; margin-bottom: 6px; color: ${INK}; }
.field input, .field select, .field textarea { width: 100%; border: 1.5px solid #887375; border-radius: 12px; padding: 12px 13px; font-size: 1rem; font-family: inherit; background: #fff; outline: none; color: ${INK}; }
.field input:focus, .field select:focus, .field textarea:focus { border-color: ${CRIMSON}; box-shadow: 0 0 0 3px rgba(166,25,46,.15); }
.field textarea { min-height: 112px; resize: vertical; }
.field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.form-msg { padding: 12px 14px; border-radius: 12px; font-size: .94rem; margin-bottom: 14px; }
.form-msg.ok { background: #EAF5EE; color: #1E5C3A; }
.form-msg.err { background: #FBECEE; color: #8E1526; }

/* Pricing */
.pricing-toggle { display: inline-flex; align-items: center; background: #fff; border: 1px solid rgba(94,13,25,.12); border-radius: 999px; padding: 4px; margin: 0 auto 40px; }
.pricing-toggle button { border: none; background: none; padding: 9px 20px; border-radius: 999px; font-weight: 600; font-size: .92rem; cursor: pointer; color: ${MUTED}; font-family: inherit; }
.pricing-toggle button.active { background: ${CRIMSON}; color: #fff; }
.pricing-toggle .save { color: #FFE5A0; }
.pricing-toggle button:not(.active) .save { color: ${CRIMSON}; }
.pricing-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 22px; align-items: stretch; }
.price-card { background: #fff; border: 1px solid rgba(94,13,25,.1); border-radius: 22px; padding: 32px 26px; display: flex; flex-direction: column; position: relative; }
.price-card.featured { border: 2px solid ${CRIMSON}; box-shadow: 0 20px 44px rgba(166,25,46,.14); }
.price-badge { position: absolute; top: -14px; left: 26px; background: ${GOLD}; color: ${CRIMSON_DARK}; font-size: .78rem; font-weight: 700; padding: 5px 14px; border-radius: 999px; white-space: nowrap; }
.price-card h3 { font-size: 1.65rem; margin-bottom: 6px; }
.price-card .tier-band { color: ${MUTED}; font-size: .92rem; margin-bottom: 20px; min-height: 44px; }
.price-amount { display: flex; align-items: baseline; gap: 5px; margin-bottom: 2px; }
.price-amount .amt { font-family: 'Fraunces', serif; font-size: 2.9rem; font-weight: 600; color: ${CRIMSON_DARK}; line-height: 1; }
.price-amount .per { color: ${MUTED}; font-size: .95rem; }
.price-sub { color: ${MUTED}; font-size: .86rem; margin-bottom: 22px; min-height: 18px; }
.price-feats { list-style: none; padding: 0; margin: 0 0 26px; flex: 1; }
.price-feats li { display: flex; gap: 9px; align-items: flex-start; font-size: .94rem; margin-bottom: 11px; }
.price-feats svg { color: ${CRIMSON}; font-size: 19px; margin-top: 2px; flex-shrink: 0; }
.price-foot { text-align: center; color: ${MUTED}; font-size: .88rem; margin: 34px auto 0; max-width: 720px; }
.addons { display: grid; grid-template-columns: repeat(3,1fr); gap: 18px; margin-top: 16px; }
.addon { background: ${CREAM_ALT}; border-radius: 16px; padding: 22px; }
.addon h4 { font-size: 1.1rem; margin-bottom: 6px; }
.addon p { color: ${MUTED}; font-size: .9rem; }

/* Get-started wizard */
.container.narrow { max-width: 640px; }
.wizard-steps { list-style: none; padding: 0; display: flex; gap: 14px; justify-content: center; margin-bottom: 24px; flex-wrap: wrap; }
.wizard-step { display: inline-flex; align-items: center; gap: 8px; font-size: .88rem; font-weight: 600; color: ${MUTED}; }
.wizard-step.active { color: ${CRIMSON_DEEP}; }
.wizard-step.done { color: ${CRIMSON}; }
.wizard-dot { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 999px; background: #fff; border: 1.5px solid rgba(94,13,25,.2); font-size: .8rem; }
.wizard-step.active .wizard-dot { background: ${CRIMSON}; color: #fff; border-color: ${CRIMSON}; }
.wizard-step.done .wizard-dot { background: ${GOLD}; color: ${CRIMSON_DARK}; border-color: ${GOLD}; }
.wizard-nav { display: flex; justify-content: space-between; gap: 12px; margin-top: 8px; }
.wizard-nav .btn { flex: 1; }
.form-card.center { text-align: center; display: flex; flex-direction: column; align-items: center; }
.subdomain-input { display: flex; align-items: stretch; border: 1.5px solid #887375; border-radius: 12px; overflow: hidden; background: #fff; }
.subdomain-input:focus-within { border-color: ${CRIMSON}; box-shadow: 0 0 0 3px rgba(166,25,46,.15); }
.subdomain-input input { border: none !important; border-radius: 0 !important; box-shadow: none !important; flex: 1; min-width: 0; }
.subdomain-suffix { display: inline-flex; align-items: center; padding: 0 14px; background: ${CREAM_ALT}; color: ${MUTED}; font-size: .94rem; white-space: nowrap; }
.subdomain-hint { display: flex; align-items: center; gap: 6px; font-size: .88rem; margin-top: 7px; }
.subdomain-hint.available { color: #1E7A45; }
.subdomain-hint.taken, .subdomain-hint.invalid { color: #A11B2E; }
.subdomain-hint.checking { color: ${MUTED}; }
.color-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.color-pick { display: inline-flex; align-items: center; gap: 9px; font-size: .86rem; cursor: pointer; }
.color-pick input[type=color] { width: 44px; height: 44px; border: 1px solid rgba(0,0,0,.15); border-radius: 10px; background: none; cursor: pointer; padding: 2px; }
.color-pick code { color: ${MUTED}; font-size: .78rem; }
.color-preview { width: 58px; height: 44px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-family: 'Fraunces', serif; font-size: 1.3rem; font-weight: 600; margin-left: auto; }

/* Sign-in / find community */
.find { display: grid; grid-template-columns: 1.15fr .85fr; gap: 40px; align-items: start; }
.find .form-card h2 { font-size: 1.6rem; margin-bottom: 6px; }
.find-go { display: flex; gap: 10px; }
.find-go .subdomain-input { flex: 1; min-width: 0; }
.found { margin-top: 18px; border: 1.5px solid rgba(166,25,46,.2); border-radius: 16px; padding: 16px; display: flex; gap: 14px; align-items: center; }
.found-mark { width: 46px; height: 46px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; color: #fff; font-family: 'Fraunces', serif; font-weight: 600; flex-shrink: 0; overflow: hidden; }
.found-mark img { width: 100%; height: 100%; object-fit: cover; }
.found b { display: block; }
.found span { color: ${MUTED}; font-size: .88rem; word-break: break-all; }
.found .btn { margin-left: auto; flex-shrink: 0; }
.side-stack { display: flex; flex-direction: column; gap: 18px; }
.side-card { background: ${CREAM_ALT}; border-radius: 20px; padding: 24px; }
.side-card h3 { font-size: 1.2rem; margin-bottom: 6px; }
.side-card p { color: ${MUTED}; font-size: .94rem; margin-bottom: 14px; }

/* ── Footer ── */
.footer { background: ${CRIMSON_DARK}; color: #E9CFD3; padding: 64px 0 28px; }
.footer-grid { display: grid; grid-template-columns: 1.6fr 1fr 1.4fr 1fr; gap: 32px; }
.footer h2 { color: #fff; font-family: 'Instrument Sans', sans-serif; font-size: .95rem; font-weight: 700; margin-bottom: 14px; letter-spacing: 0; }
.kk .footer a, .kk .footer a:visited { color: #E9CFD3; }
.footer-col a { display: block; color: #E9CFD3; font-size: .93rem; padding: 5px 0; }
.footer-col a:hover { color: #fff; text-decoration: underline; text-underline-offset: 3px; }
.footer-brand .brand-mark { background: #fff; border-radius: 50%; padding: 3px; }
.footer-tag { color: ${GOLD}; font-size: .9rem; margin-top: 14px; }
.footer-brand p { font-size: .92rem; margin: 10px 0 20px; max-width: 300px; }
.footer-bottom { display: flex; justify-content: space-between; gap: 12px; border-top: 1px solid rgba(255,255,255,.14); margin-top: 44px; padding-top: 20px; font-size: .85rem; color: #D7B6BB; flex-wrap: wrap; }
.footer-bottom nav { display: flex; gap: 18px; flex-wrap: wrap; }
.kk .footer a:hover { color: #fff; text-decoration: underline; text-underline-offset: 3px; }
.footer-bottom a { display: inline-block; min-height: 24px; }

@media (prefers-reduced-motion: no-preference) {
  .hero-copy > * { animation: kk-rise .6s cubic-bezier(.2,.7,.2,1) both; }
  .hero-copy > *:nth-child(2) { animation-delay: .06s; }
  .hero-copy > *:nth-child(3) { animation-delay: .12s; }
  .hero-copy > *:nth-child(4) { animation-delay: .18s; }
  .ring .draw { stroke-dasharray: 1600; stroke-dashoffset: 1600; animation: kk-draw 1.6s .2s cubic-bezier(.6,0,.2,1) forwards; }
}
@keyframes kk-rise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes kk-draw { to { stroke-dashoffset: 0; } }

@media (max-width: 1040px) {
  .nav-links { display: none; }
  .nav-actions .hide-sm { display: none; }
  .nav-toggle { display: inline-flex; }
  .nav-drawer.open { display: block; border-top: 1px solid rgba(94,13,25,.08); background: ${CREAM}; }
  .nav-drawer nav { display: flex; flex-direction: column; padding: 8px 28px 20px; }
  .nav-drawer a { padding: 12px 0; font-weight: 500; border-bottom: 1px solid rgba(94,13,25,.06); }
  .nav-drawer .btn { margin-top: 14px; border-bottom: none; }
  .hero-inner, .hier, .brands, .find, .contact-grid { grid-template-columns: 1fr; }
  .hero-visual { min-height: 520px; }
  .feat-cols { grid-template-columns: repeat(2,1fr); }
  .feat-col:nth-child(3) { padding-left: 0; border-left: none; }
  .feat-col:nth-child(n+3) { border-top: 1px solid rgba(94,13,25,.12); }
  .steps { grid-template-columns: repeat(2,1fr); }
  .steps li::after { display: none; }
  .grid-4 { grid-template-columns: repeat(2,1fr); }
  .grid-3 { grid-template-columns: repeat(2,1fr); }
  .pricing-grid, .addons { grid-template-columns: 1fr; }
  .price-card .tier-band { min-height: 0; }
  .footer-grid { grid-template-columns: 1fr 1fr; }
  .footer-brand { grid-column: span 2; }
}
@media (max-width: 360px) {
  .nav .brand-word { font-size: .9rem; letter-spacing: .15em; }
  .nav .brand { gap: 6px; }
}
@media (max-width: 720px) {
  .container { padding: 0 16px; }
  .band { padding: 64px 0; }
  .hero { padding: 36px 0 56px; }
  .nav-inner { gap: 12px; }
  .nav-actions { gap: 8px; }
  .nav-actions .btn { white-space: nowrap; }
  .nav-actions .btn-primary { display: none; }
  .nav-actions .btn-outline { padding: 9px 16px; }
  .paths { grid-template-columns: 1fr; }
  .path { padding: 32px 24px; }
  .final { grid-template-columns: 1fr; gap: 18px; }
  .final img { width: 92px; }
}
@media (max-width: 560px) {
  .brand-word { font-size: .98rem; letter-spacing: .2em; }
  .hero-visual { min-height: 480px; }
  .phone { width: 238px; }
  .float-card { max-width: 170px; font-size: .74rem; padding: 10px 12px; }
  .hero-visual .phone { margin-right: 96px; }
  .ring { left: calc(50% - 48px); }
  .float-chat { top: 40px; right: 0; }
  .float-tap { bottom: 40px; right: 0; }
  .feat-cols, .grid-4, .grid-3, .steps { grid-template-columns: 1fr; }
  .feat-col, .feat-col + .feat-col { padding-left: 0; border-left: none; }
  .feat-col + .feat-col { border-top: 1px solid rgba(94,13,25,.12); }
  .minis { gap: 10px; }
  .mini { width: 31%; min-width: 0; border-radius: 20px; padding: 4px; }
  .mini .screen { border-radius: 16px; }
  .mini-head { padding: 16px 8px 10px; }
  .mini-head span { display: none; }
  .mini:nth-child(2) { transform: translateY(-14px); }
  .field-row { grid-template-columns: 1fr; }
  .form-card { padding: 22px 18px; }
  .find-go { flex-direction: column; }
  .subdomain-suffix { padding: 0 10px; font-size: .86rem; }
  .found { flex-wrap: wrap; }
  .found .btn { margin-left: 0; width: 100%; }
  .footer-grid { grid-template-columns: 1fr; }
  .footer-brand { grid-column: auto; }
  .tree { padding: 22px 10px; }
  .node { font-size: .7rem; padding: 7px 8px; }
}
`;
